import { useState } from 'react';
import { Modal } from '@round/whitelabel-ui';
import PostForm, { PostFormValues, getValidationSchema } from '../PostForm/PostForm';
import { Button } from '@round/whitelabel-ui';
import { FormikProvider, useFormik } from 'formik';
import { creatorbase } from '@round/api';
import { showNotification } from 'helpers';
import styles from './CreatePostModal.module.css';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import omit from 'lodash/omit';
import useProjectDetails from '../../useProjectDetails';
import { useClient, useClientInfluencer } from '@round/creatorbase-helpers/api-hooks';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import CreatorCostMismatchBanner from 'Modules/Plans/Posts/components/CreatorCostMismatchBanner/CreatorCostMismatchBanner';
import { getClientInfluencerPlatformCost } from 'Modules/Plans/Posts/helpers';
import { MismatchAfterSave, getMismatchAfterSave } from './helpers';

type Props = {
    isOpen: boolean;
    closeModal: () => void;
    createPost: typeof creatorbase.postPost;
    campaign: creatorbase.Campaign | null | undefined;
};

const CreatePostModal = ({ isOpen, closeModal, createPost, campaign }: Props) => {
    const { team } = useProjectDetails();
    const { client } = useClient(team?.client_id);
    const { getCurrencyOptionsById } = useCurrencies();

    const areFeesEnabled = !!client?.is_payment_enabled;

    const [mismatchAfterSave, setMismatchAfterSave] = useState<MismatchAfterSave | null>(null);

    // Sourced live (rather than frozen at save time) so that updating the creator's cost from the
    // banner - which invalidates this same query - makes the mismatch, and the banner, disappear.
    const { data: clientInfluencerForMismatch } = useClientInfluencer(mismatchAfterSave?.clientInfluencerId, {
        enabled: !!mismatchAfterSave,
    });
    const creatorPlatformCostForMismatch = mismatchAfterSave
        ? getClientInfluencerPlatformCost(clientInfluencerForMismatch, mismatchAfterSave.platform)
        : null;

    const formik = useFormik<PostFormValues>({
        initialValues: {
            identifier: '',
            influencer_account_identifier: '',
            cost: undefined,
            currency: getCurrencyOptionsById(campaign?.currency_id),
            isLivePost: undefined,
            creator: null,
        },
        onSubmit: async (values, helpers) => {
            if (!campaign) {
                return;
            }

            const clientInfluencerAccountId =
                campaign.platform !== 'youtube'
                    ? values.creator?.platforms[campaign.platform][0]?.creatorbase_client_influencer_account_id
                    : undefined;
            try {
                const payload: creatorbase.PostPostData = {
                    campaign_id: campaign.id,
                    identifier: values.identifier,
                    influencer_account_identifier: values.influencer_account_identifier,
                    client_influencer_account_id: clientInfluencerAccountId,
                    // When fees are enabled we provdide the cost value to the base_cost field instead; the BE handles adding the fees.
                    ...(areFeesEnabled
                        ? { base_cost: values.cost, currency_id: values.currency?.value }
                        : { cost: values.cost }),
                };

                const propertiesToDelete = values.isLivePost
                    ? ['influencer_account_identifier', 'client_influencer_account_id']
                    : [
                          'identifier',
                          !clientInfluencerAccountId ? 'client_influencer_account_id' : 'influencer_account_identifier',
                      ];

                const response = await createPost(omit(payload, propertiesToDelete) as creatorbase.PostPostData);
                if (response.status === 201) {
                    showNotification('Post created', 'info');

                    const mismatch = getMismatchAfterSave({
                        platform: campaign.platform,
                        creator: values.creator,
                        cost: values.cost,
                        currencyId: values.currency?.value,
                    });

                    helpers.resetForm();

                    if (mismatch) {
                        setMismatchAfterSave(mismatch);
                        return;
                    }

                    closeModal();
                    return;
                }

                if (response.status === 400) {
                    helpers.setErrors(mapApiErrorsToFormikErrors(response.data));
                    return;
                }

                showNotification('Could not create post', 'error');
            } catch {
                showNotification('Could not create post', 'error');
            }
        },
        validationSchema: getValidationSchema(Boolean(campaign?.is_post_cost_editable)),
        enableReinitialize: true,
    });

    const handleClose = () => {
        formik.resetForm();
        setMismatchAfterSave(null);
        closeModal();
    };

    return (
        <Modal className={styles.modal} closeOnOverlayClick isOpen={isOpen} onClose={handleClose}>
            <Modal.Header>
                <Modal.Title>Create Post</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FormikProvider value={formik}>
                    <PostForm
                        isPostCostEditable={campaign?.is_post_cost_editable}
                        campaignCurrency={campaign?.currency_id}
                        platform={campaign?.platform}
                        areFeesEnabled={areFeesEnabled}
                        campaignId={campaign?.id}
                        creatorCostMismatchBanner={
                            mismatchAfterSave && creatorPlatformCostForMismatch ? (
                                <CreatorCostMismatchBanner
                                    clientInfluencerId={mismatchAfterSave.clientInfluencerId}
                                    platform={mismatchAfterSave.platform}
                                    creatorCost={creatorPlatformCostForMismatch.cost}
                                    creatorCurrencyId={creatorPlatformCostForMismatch.currencyId}
                                    paymentCost={mismatchAfterSave.paymentCost}
                                    paymentCurrencyId={mismatchAfterSave.paymentCurrencyId}
                                />
                            ) : null
                        }
                    />
                </FormikProvider>
            </Modal.Body>
            <Modal.Actions>
                <Button
                    appearance="primary"
                    isLoading={formik.isSubmitting}
                    disabled={!formik.dirty || !formik.isValid}
                    onClick={formik.submitForm}
                >
                    Create post
                </Button>

                <Button appearance="ghost" onClick={handleClose}>
                    Cancel
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default CreatePostModal;
